import {
  FC,
  PropsWithChildren,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { useBlockNoteEditor, useEditorState } from '@blocknote/react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import FormatUnderlined from '@mui/icons-material/FormatUnderlined';
import TableChart from '@mui/icons-material/TableChart';
import Link from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Row } from '../../Layout';
import {
  editorLinkHrefToDisplayInput,
  normalizeEditorLinkHref,
} from '../helpers/editor-link-url';
import isLinkMarkActiveAtSelection from '../helpers/is-link-mark-active-at-selection';
import getLinkUrlAtSelection from '../helpers/get-link-url-at-selection';
import setLinkHrefAtSelection from '../helpers/set-link-href-at-selection';
import unsetLinkMark from '../helpers/unset-link-mark';
import ensureParagraphAfterBlockAndFocus from '../helpers/ensure-paragraph-after-block-and-focus';
import { BlockSchema } from '../schema';

const AppBar = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  paddingBottom: theme.spacing(theme.spacingSizes.xs),
  marginBottom: theme.spacing(theme.spacingSizes.sm),
  position: 'sticky',
  top: 0,
}));

const ButtonGroup = styled(Row)({
  gap: '2px',
});

const ToolbarIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  padding: theme.spacing(theme.spacingSizes.xxs),
  borderRadius: 4,
  backgroundColor: active ? '#efefef' : 'transparent',
  '&:hover': {
    backgroundColor: active ? '#e9e9e9' : 'transparent',
  },
}));

const LinkToolbarRoot = styled(Box)({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

const LinkToolbarPaper = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: theme.spacing(0.5),
  zIndex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(theme.spacingSizes.xxs),
  padding: theme.spacing(theme.spacingSizes.xs),
  minWidth: 260,
  backgroundColor: theme.palette.background.paper,
}));

const LinkToolbarFieldWrap = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const LinkToolbarIconActions = styled(Box)({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: '2px',
});

const LinkToolbarIconButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(theme.spacingSizes.xxs),
  borderRadius: '50%',
  color: 'inherit',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Check = styled(CheckIcon)(({ theme }) => ({
  color: theme.palette.success.main,
}));

const LinkOff = styled(LinkOffIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const MarkButton: FC<
  PropsWithChildren<{
    tooltip?: string;
    active: boolean;
    onClick: () => void;
  }>
> = ({ tooltip, active, onClick, children }) => (
  <Tooltip title={tooltip} arrow>
    <ToolbarIconButton
      active={active}
      size="small"
      onClick={onClick}
      color={active ? 'primary' : 'default'}
    >
      {children}
    </ToolbarIconButton>
  </Tooltip>
);

const BoldButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const active = useEditorState({
    editor,
    selector: ({ editor }) => 'bold' in editor.getActiveStyles(),
  });

  const onClick = () => {
    editor.toggleStyles({ bold: true });
    editor.focus();
  };

  return (
    <MarkButton tooltip="Bold" active={active} onClick={onClick}>
      <FormatBold fontSize="small" />
    </MarkButton>
  );
};

const ItalicButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const active = useEditorState({
    editor,
    selector: ({ editor }) => 'italic' in editor.getActiveStyles(),
  });

  const onClick = () => {
    editor.toggleStyles({ italic: true });
    editor.focus();
  };

  return (
    <MarkButton tooltip="Italic" active={active} onClick={onClick}>
      <FormatItalic fontSize="small" />
    </MarkButton>
  );
};

const UnderlineButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const active = useEditorState({
    editor,
    selector: ({ editor }) => 'underline' in editor.getActiveStyles(),
  });

  const onClick = () => {
    editor.toggleStyles({ underline: true });
    editor.focus();
  };

  return (
    <MarkButton tooltip="Underline" active={active} onClick={onClick}>
      <FormatUnderlined fontSize="small" />
    </MarkButton>
  );
};

const BulletListButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const active = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor.getTextCursorPosition().block.type === 'bulletListItem',
  });

  const onClick = () => {
    const block = editor.getTextCursorPosition().block;
    editor.updateBlock(block, {
      type: active ? 'paragraph' : 'bulletListItem',
    });
    editor.focus();
  };

  return (
    <MarkButton tooltip="Bullet-point list" active={active} onClick={onClick}>
      <FormatListBulleted fontSize="small" />
    </MarkButton>
  );
};

const NumberedListButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const active = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor.getTextCursorPosition().block.type === 'numberedListItem',
  });

  const onClick = () => {
    const block = editor.getTextCursorPosition().block;
    editor.updateBlock(block, {
      type: active ? 'paragraph' : 'numberedListItem',
    });
    editor.focus();
  };

  return (
    <MarkButton tooltip="Ordered list" active={active} onClick={onClick}>
      <FormatListNumbered fontSize="small" />
    </MarkButton>
  );
};

const TableButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();

  const onClick = () => {
    const cursorBlock = editor.getTextCursorPosition().block;
    const [tableBlock] = editor.insertBlocks(
      [
        {
          type: 'table',
          content: {
            type: 'tableContent',
            rows: [
              { cells: ['', '', ''] },
              { cells: ['', '', ''] },
              { cells: ['', '', ''] },
            ],
          },
        },
      ],
      cursorBlock,
      'after',
    );
    ensureParagraphAfterBlockAndFocus(editor, tableBlock);
  };

  return (
    <MarkButton tooltip="Table" active={false} onClick={onClick}>
      <TableChart fontSize="small" />
    </MarkButton>
  );
};

const LinkButton: FC = () => {
  const editor = useBlockNoteEditor<BlockSchema>();
  const [open, setOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const active = useEditorState({
    editor,
    selector: () => isLinkMarkActiveAtSelection(editor),
  });

  const handleClose = () => {
    setOpen(false);
  };

  const handleLinkButtonClick = () => {
    if (open) {
      handleClose();
      return;
    }
    setUrlInput(editorLinkHrefToDisplayInput(getLinkUrlAtSelection(editor)));
    setOpen(true);
  };

  const removeLink = () => {
    unsetLinkMark(editor);
    setUrlInput('');
    handleClose();
    editor.focus();
  };

  const applyLink = () => {
    const trimmed = urlInput.trim();
    if (trimmed === '') {
      removeLink();
      return;
    }
    const href = normalizeEditorLinkHref(trimmed);
    if (href === null) {
      editor.focus();
      return;
    }
    const isUpdatingExistingLink =
      isLinkMarkActiveAtSelection(editor) ||
      getLinkUrlAtSelection(editor) !== '';

    if (isUpdatingExistingLink) {
      setLinkHrefAtSelection(editor, href);
      handleClose();
    } else {
      const hasTextSelection = editor.getSelectedText().length > 0;
      if (hasTextSelection) {
        editor.createLink(href);
      } else {
        editor.createLink(href, href);
      }
      handleClose();
    }
    editor.focus();
  };

  const handleUrlFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyLink();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      handleClose();
    }
  };

  const handleLinkToolbarPaperMouseDown = (event: MouseEvent) => {
    if (
      event.target instanceof Element &&
      (event.target.closest('input') || event.target.closest('textarea'))
    ) {
      return;
    }
    event.preventDefault();
  };

  const openLinkInNewTab = () => {
    const trimmed = urlInput.trim();
    if (trimmed === '') {
      return;
    }
    const href = normalizeEditorLinkHref(trimmed);
    if (href === null) {
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <LinkToolbarRoot>
        <MarkButton
          tooltip="Link"
          active={active || open}
          onClick={handleLinkButtonClick}
        >
          <Link fontSize="small" />
        </MarkButton>
        {open ? (
          <LinkToolbarPaper
            elevation={0}
            variant="outlined"
            onMouseDown={handleLinkToolbarPaperMouseDown}
          >
            <LinkToolbarFieldWrap>
              <TextField
                label="Link"
                size="small"
                value={urlInput}
                onChange={(event) => {
                  setUrlInput(event.target.value);
                }}
                autoFocus
                variant="outlined"
                fullWidth
                onKeyDown={handleUrlFieldKeyDown}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </LinkToolbarFieldWrap>
            <LinkToolbarIconActions>
              <Tooltip title="Open in new tab">
                <LinkToolbarIconButton
                  size="small"
                  aria-label="Open link in new tab"
                  onClick={openLinkInNewTab}
                >
                  <OpenInNewIcon fontSize="small" />
                </LinkToolbarIconButton>
              </Tooltip>
              <Tooltip title="Apply">
                <LinkToolbarIconButton
                  size="small"
                  aria-label="Apply link"
                  onClick={applyLink}
                >
                  <Check fontSize="small" />
                </LinkToolbarIconButton>
              </Tooltip>
              <Tooltip title="Remove link">
                <LinkToolbarIconButton
                  size="small"
                  aria-label="Remove link"
                  onClick={removeLink}
                >
                  <LinkOff fontSize="small" />
                </LinkToolbarIconButton>
              </Tooltip>
            </LinkToolbarIconActions>
          </LinkToolbarPaper>
        ) : null}
      </LinkToolbarRoot>
    </ClickAwayListener>
  );
};

type Props = {
  showTable?: boolean;
};

export const MUIFormattingToolbar: FC<Props> = ({ showTable = true }) => (
  <AppBar>
    <Row centered spacing="xs">
      <ButtonGroup centered>
        <BoldButton />
        <ItalicButton />
        <UnderlineButton />
      </ButtonGroup>
      <Divider orientation="vertical" flexItem />
      <ButtonGroup centered>
        <BulletListButton />
        <NumberedListButton />
      </ButtonGroup>
      {showTable ? (
        <>
          <Divider orientation="vertical" flexItem />
          <ButtonGroup centered>
            <TableButton />
          </ButtonGroup>
        </>
      ) : null}
      <Divider orientation="vertical" flexItem />
      <LinkButton />
    </Row>
  </AppBar>
);
